/**
 * rematchResolution.ts — pure, dependency-injected handling of the
 * authoritative REJECTED rematch resolution (phaser:rematch-resolved).
 *
 * No React, no router, no DOM: every side effect (guard stand-down,
 * active-game invalidation, navigation) is injected, so the full ordering is
 * unit-testable in jsdom without rendering GameScreen (which boots Phaser).
 * GameScreen is the thin adapter — the same pattern as leaveDecision.ts /
 * useLeaveGameGuard.
 *
 * Why this module exists (double-exit defect, 2026-08-23 local QA):
 *   1. GameScene emits the LEGACY bot destination '/play/bots', but the route
 *      table has no such path — the catch-all bounced players to /home
 *      instead of the real bots lobby at '/play/ai'.
 *   2. Nothing in the rejection path invalidated ActiveGameContext, so a
 *      previously solo-saved session kept showing the stale "BOT GAME SAVED /
 *      Continue Game" surface for a match the backend had already finished
 *      (backend verified correct: /home returns active_game: null).
 *   3. Re-entering the finished game (stale banner, deep link, reconnect)
 *      resolves the rejected rematch WITHOUT a round-end payload, so the
 *      leave guard's matchCompleted stand-down was false and the resolution
 *      navigation got intercepted by the "Leave this game?" modal — the
 *      observed second Leave Game prompt.
 *
 * Follow-up correction (mode-specific decline destinations, 2026-08-23 local
 * QA): the first repair above translated '/play/bots' to '/play/ai'. But
 * '/play/ai' unconditionally auto-creates a fresh bot session on mount
 * (PlayAIScreen's startGame effect) — so a declined rematch appeared to
 * silently start an unwanted new bot match. Confirmed live: session 458
 * (rematch correctly rejected, rematch_session_id nil) was immediately
 * followed by session 459/game 472 — a brand-new 0-0 bot session with no
 * rematch_state at all, i.e. a fresh /play/ai launch, not a rematch
 * successor. The corrected mapping sends bot-match declines to /home (no
 * auto-start; a new bot match requires an explicit Play Bots click) and
 * online declines to /play/online, which only ever creates/joins a table
 * from an explicit user click (verified against PlayOnlineScreen — its
 * mount effect only reads the session list).
 *
 * The fix keeps Rails authoritative: nothing here decides rejection — it only
 * enacts the navigation for a rejection GameScene already resolved from the
 * authoritative snapshot.
 */

/**
 * GameScene (protected file) still emits a route string built from its
 * authoritative _finalGameMode ('/play/bots' | '/play/online') — a
 * deterministic 1:1 encoding of the game mode, not an arbitrary destination.
 * This decodes that signal back into the real GameMode rather than treating
 * the emitted string as a navigable path in its own right.
 */
type GameMode = 'bots' | 'online';

function decodeEmittedMode(route: unknown): GameMode | null {
  if (route === '/play/bots')   return 'bots';
  if (route === '/play/online') return 'online';
  return null;
}

/**
 * The application's own explicit mode → destination mapping (real route
 * constants, never invented): bot-match declines go Home (no route
 * auto-starts a game); online declines go to the real Play Online
 * table-creation/lobby screen (verified non-auto-start).
 */
const MODE_DESTINATION: Record<GameMode, string> = {
  bots:   '/home',
  online: '/play/online',
};

/**
 * Map an emitted rematch-resolution route to the real, mode-owned
 * destination. Anything that doesn't decode to a known game mode is
 * rejected (event details are data, not trusted navigation).
 */
export function resolveRematchDestination(route: unknown): string | null {
  const mode = decodeEmittedMode(route);
  return mode ? MODE_DESTINATION[mode] : null;
}

export interface RematchResolvedDeps {
  /** Stand the leave guard down for this completed match BEFORE navigating —
   *  the reconnect-into-rejected path has no round-end payload, so without
   *  this the guard would intercept the resolution navigation with the
   *  active-game "Leave this game?" modal. Must take effect synchronously
   *  (GameScreen uses flushSync so the guard's render-captured state updates
   *  before navigate runs its blocker predicate). */
  markResolved: () => void;
  /** Invalidate the global active-game/rejoin state (ActiveGameContext
   *  refetches from the authoritative /home payload, which the backend has
   *  already cleared for a finished session). Fired atomically with the
   *  navigation — never suppressed with CSS, never guessed client-side. */
  invalidateActiveGame: () => void;
  /** Perform the single route change. */
  navigate: (route: string) => void;
}

/**
 * Enact one authoritative rejected-rematch resolution. Returns true when a
 * navigation was performed. Ordering is load-bearing:
 *   markResolved → invalidateActiveGame → navigate
 * so the guard cannot intercept and the destination can never render the
 * completed match as rejoinable from stale context.
 *
 * Idempotence: GameScene dedupes the dispatch per scene lifecycle
 * (_rematchHandled); a repeated call here simply re-navigates to the same
 * destination with replace semantics — harmless, and a NEW visit to an
 * already-rejected match is *supposed* to route away again.
 */
export function handleRematchResolved(detail: unknown, deps: RematchResolvedDeps): boolean {
  const route = resolveRematchDestination((detail as { route?: unknown } | null | undefined)?.route);
  if (route === null) return false;
  deps.markResolved();
  deps.invalidateActiveGame();
  deps.navigate(route);
  return true;
}
